import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import styles from "../WebsiteTemplatePreview.module.scss";
import { getAmenityIconNode } from "../amenityIconRegistry";
import {
  getInteractiveTargetProps,
  getPreviewTargetMarkerProps,
  TemplateAvailabilityCalendar,
  TemplateHeroCopy,
  TemplateImageSlotVisual,
  TemplateSoftCallout,
  TemplateTopBar,
} from "./templateSharedSections";
import {
  amenityPropType,
  calendarSectionPropType,
  availabilityPropType,
  callToActionPropType,
  copyItemPropType,
  galleryPropType,
  heroPropType,
  mediaPropType,
  sitePropType,
  templateInteractionPropTypes,
  visibilityPropType,
  quoteContextPropType,
} from "./templatePropTypes";
import { resolveWebsiteAmenityIconColor } from "../../config/websiteAmenitiesConfig";
import { resolveQuotePanelSiteId } from "../booking/quotePanelGate";

const LazyQuoteAvailabilitySection = lazy(() => import("../booking/QuoteAvailabilitySection"));

export default function ExperienceJourneyTemplate({ model, onSelectTarget, activeTargetId, quoteContext = null }) {
  const showTopBar = model.visibility?.topBar !== false;
  const showJourneyStops = model.visibility?.journeyStops !== false;
  const showAmenitiesPanel = model.visibility?.amenitiesPanel !== false;
  const showAvailabilityCalendar = model.visibility?.availabilityCalendar !== false;
  const showCallToAction = model.visibility?.callToAction !== false;
  const showExperienceFooter = showAmenitiesPanel || showCallToAction;
  const amenityIconColor = resolveWebsiteAmenityIconColor(
    model.amenities?.iconColor,
    "experience-journey"
  );
  const quotePanelSiteId = resolveQuotePanelSiteId({ quoteContext, model, onSelectTarget });

  const availabilityCalendar = (
    <TemplateAvailabilityCalendar
      model={model}
      templateKey="experience-journey"
      onSelectTarget={onSelectTarget}
      activeTargetId={activeTargetId}
    />
  );
  const availabilityContent = quotePanelSiteId ? (
    <Suspense fallback={availabilityCalendar}>
      <LazyQuoteAvailabilitySection model={model} siteId={quotePanelSiteId} templateKey="experience-journey" />
    </Suspense>
  ) : (
    availabilityCalendar
  );

  return (
    <article className={styles.templateSite}>
      {showTopBar ? (
        <TemplateTopBar model={model} onSelectTarget={onSelectTarget} activeTargetId={activeTargetId}>
          <div className={styles.templateTopBarNav}>
            <span>Arrival</span>
            <span>Stay</span>
            <span>Area</span>
          </div>
        </TemplateTopBar>
      ) : null}

      <section className={styles.experienceIntro}>
        <TemplateHeroCopy
          className={styles.experienceIntroCopy}
          model={model}
          onSelectTarget={onSelectTarget}
          activeTargetId={activeTargetId}
        />
      </section>

      {showJourneyStops ? (
        <section
          {...getPreviewTargetMarkerProps(
            styles.experienceJourneyStack,
            "visibility.journeyStops",
            activeTargetId
          )}
        >
          {model.journeyStops.slice(0, 3).map((stop, index) => {
            const isReversed = index % 2 === 1;

            return (
              <article
                key={stop.id}
                {...getInteractiveTargetProps(
                  `${styles.experienceJourneyRow} ${
                    isReversed ? styles.experienceJourneyRowReversed : ""
                  }`.trim(),
                  onSelectTarget,
                  {
                    sectionId: "journeyStops",
                    targetId: `journeyStops.${index}`,
                  },
                  activeTargetId
                )}
              >
                <div className={styles.experienceJourneyCopy}>
                  <p className={styles.journeyLabel}>{stop.title}</p>
                  <p>{stop.description}</p>
                </div>
                <TemplateImageSlotVisual
                  model={model}
                  slot={{ kind: "gallery", index }}
                  imageClassName={styles.experienceJourneyVisual}
                  alt={`${stop.title} visual`}
                  onSelectTarget={onSelectTarget}
                  activeTargetId={activeTargetId}
                />
              </article>
            );
          })}
        </section>
      ) : null}

      {showAvailabilityCalendar ? availabilityContent : null}

      {showExperienceFooter ? (
        <section className={styles.sectionCard}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionEyebrow}>Imported highlights</p>
            <h2>Featured amenities and next step</h2>
          </div>

          <div className={styles.experienceFooter}>
            {showAmenitiesPanel ? (
              <div
                {...getInteractiveTargetProps(styles.amenityList, onSelectTarget, {
                  sectionId: "amenities",
                  targetId: "visibility.amenitiesPanel",
                }, activeTargetId)}
              >
                {model.amenities.featured.slice(0, 4).map((amenity, index) => {
                  const amenityIcon = getAmenityIconNode(amenity.iconAmenityId || amenity.id, {
                    className: styles.amenityRowIconGlyph,
                    "aria-hidden": true,
                    focusable: "false",
                    sx: {
                      color: amenityIconColor,
                      fontSize: 18,
                      padding: 0,
                    },
                  });

                  return (
                    <div
                      key={amenity.id}
                      {...getInteractiveTargetProps(styles.amenityRow, onSelectTarget, {
                        sectionId: "amenities",
                        targetId: `amenities.${index}`,
                      }, activeTargetId)}
                    >
                      <div className={styles.amenityRowPrimary}>
                        {amenityIcon ? <span className={styles.amenityRowIcon}>{amenityIcon}</span> : null}
                        <strong>{amenity.label}</strong>
                      </div>
                      <span>{amenity.category}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {showCallToAction ? (
              <TemplateSoftCallout
                className={styles.softCallout}
                model={model}
                onSelectTarget={onSelectTarget}
                activeTargetId={activeTargetId}
              />
            ) : null}
          </div>
        </section>
      ) : null}
    </article>
  );
}

ExperienceJourneyTemplate.propTypes = {
  model: PropTypes.shape({
    site: sitePropType.isRequired,
    hero: heroPropType.isRequired,
    journeyStops: PropTypes.arrayOf(copyItemPropType).isRequired,
    gallery: galleryPropType.isRequired,
    media: mediaPropType.isRequired,
    amenities: PropTypes.shape({
      iconColor: PropTypes.string,
      featured: PropTypes.arrayOf(amenityPropType).isRequired,
    }).isRequired,
    availability: availabilityPropType.isRequired,
    calendarSection: calendarSectionPropType,
    callToAction: callToActionPropType.isRequired,
    visibility: visibilityPropType.isRequired,
  }).isRequired,
  ...templateInteractionPropTypes,
  quoteContext: quoteContextPropType,
};
